export type NationalAiResource = {
  title: string;
  supportType: string;
  workspaceSupport: string;
};

export type NationalAiScreenshotPlan = {
  id: string;
  title: string;
  caption: string;
  alt: string;
  variant: 'library' | 'policy' | 'register' | 'process' | 'team' | 'review';
};

/**
 * Keep these titles exactly as published by the National AI Centre. The list
 * covers the 11 downloads on the Planning tools and templates page plus the
 * separately published AI screening questions.
 */
export const nationalAiResources: NationalAiResource[] = [
  {
    title: 'AI screening questions',
    supportType: 'Guided activity',
    workspaceSupport: 'Answer seven questions in the workspace, save the result and return to it when the use case changes. Screening identifies attention areas; it does not produce a score or approval.',
  },
  {
    title: 'Business process mapping template',
    supportType: 'Guided activity and Word import',
    workspaceSupport: 'Map the current process, pain points and possible opportunities in the workspace, or review a completed official Word template before saving it.',
  },
  {
    title: 'Data quality checklist',
    supportType: 'Guided review and supporting-material import',
    workspaceSupport: 'Check the information needed for the AI task. Record important gaps and assign someone to follow them up. Uploading a file does not prove its contents.',
  },
  {
    title: 'Questions to ask AI suppliers',
    supportType: 'Guided review and supporting-material import',
    workspaceSupport: 'Record supplier answers, keep claims separate from reviewed evidence, and assign unresolved questions and actions.',
  },
  {
    title: 'Team readiness canvas',
    supportType: 'Guided activity and PDF import',
    workspaceSupport: 'Work through six readiness areas, include affected people, and turn gaps into actions with owners and a review point.',
  },
  {
    title: 'Test your scenario facilitator guide',
    supportType: 'Facilitated team activity',
    workspaceSupport: 'Prepare a structured session, record participants and observations, then return priority issues and actions to the AI use case.',
  },
  {
    title: 'Scenario 1: the helpful assistant',
    supportType: 'Selectable workshop scenario',
    workspaceSupport: 'Use the published scenario in a facilitated exercise and preserve what the team observed, decided and needs to do next.',
  },
  {
    title: 'Scenario 2: the invisible shortlist',
    supportType: 'Selectable workshop scenario',
    workspaceSupport: 'Use the published scenario in a facilitated exercise and preserve what the team observed, decided and needs to do next.',
  },
  {
    title: 'Scenario 3: the winning application',
    supportType: 'Selectable workshop scenario',
    workspaceSupport: 'Use the published scenario in a facilitated exercise and preserve what the team observed, decided and needs to do next.',
  },
  {
    title: 'AI policy guide and template',
    supportType: 'Guided policy and Word import',
    workspaceSupport: 'Create or review one controlled organisation AI policy, assign an owner, manage versions and connect the adopted policy to relevant AI use cases.',
  },
  {
    title: 'AI register template',
    supportType: 'Word import and shared register',
    workspaceSupport: 'Review mapped rows from the official Word template before creating or updating records in the organisation’s shared AI Register.',
  },
  {
    title: 'AI register template (spreadsheet)',
    supportType: 'Excel import, export and shared register',
    workspaceSupport: 'Review each spreadsheet row before import, resolve conflicts and keep the same controlled register fields available for export.',
  },
];

/**
 * Capture instructions are deliberately kept as source metadata rather than
 * customer-facing copy. Replace each schematic with its final image only from
 * an accepted release candidate. Use approved example records only when a catalogue entry is published; crop out
 * browser chrome and never show email addresses, IDs or other personal data.
 */
export const nationalAiScreenshotPlans: NationalAiScreenshotPlan[] = [
  {
    id: 'tools-overview',
    title: 'Choose the right starting point',
    caption: 'Start with the governance task your team needs and open the relevant guided activity.',
    alt: 'Schematic of the tools and team activities library grouped by governance task',
    variant: 'library',
  },
  {
    id: 'policy-control',
    title: 'Maintain one organisation AI policy',
    caption: 'Keep the policy’s owner, status, version and review point together.',
    alt: 'Schematic of an organisation AI policy with status, owner and review details',
    variant: 'policy',
  },
  {
    id: 'register-import',
    title: 'Bring your existing AI Register',
    caption: 'Review supported Word or Excel records before they become part of your shared register.',
    alt: 'Schematic of an AI Register import preview with mapped rows and review choices',
    variant: 'register',
  },
  {
    id: 'process-map',
    title: 'Understand the process before choosing AI',
    caption: 'Record how work happens today, where the problems are and whether AI is an appropriate response.',
    alt: 'Schematic of a business process map with current work, pain points and an AI opportunity',
    variant: 'process',
  },
  {
    id: 'team-work',
    title: 'Prepare your team',
    caption: 'Use structured activities to explore difficult situations and turn observations into follow-up actions.',
    alt: 'Schematic of a facilitated scenario workshop with observations and follow-up actions',
    variant: 'team',
  },
  {
    id: 'data-supplier',
    title: 'Keep claims and evidence clear',
    caption: 'Separate supplier statements, reviewed evidence and unanswered questions, with responsibility for follow-up.',
    alt: 'Schematic of a supplier review separating a claim, evidence, an unknown and an action',
    variant: 'review',
  },
];

// Earlier unqualified screenshot candidates were retired; current figures use
// the accepted publication manifest in learning/images.ts.
